import { EppoClient, VariationType } from '@eppo/js-client-sdk-common'
import {
  DefaultSubjectAttributes,
  transformSubjectAttributes,
} from '../../shared/DefaultSubjectAttributes'
import { tryResolveEppoSubjectKey } from '../../shared/resolveEppoSubjectKey'

export type GetAllExperimentVariants = (params: {
  subjectAttributes?: Record<string, string | number | boolean>
}) => Record<string, string | boolean | number>

export const setupGetAllExperimentVariants = ({
  client,
  sessionId,
  customerId,
  defaultAttributes,
  overrides,
}: {
  client: EppoClient | null
  sessionId: string
  customerId: string | null
  defaultAttributes: DefaultSubjectAttributes
  overrides: { [key: string]: string }
}) => {
  const getAllExperimentVariants: GetAllExperimentVariants = ({
    subjectAttributes,
  }) => {
    if (!client) return {}

    // This is needed as our shouldLog mechanism breaks caching
    // We should investigate whether we can use client.useCustomAssignmentCache instead
    client.disableAssignmentCache()

    const allFlags = client.getFlagKeys()
    const flagConfigurations = client.getFlagConfigurations()
    const experiments: Record<string, string> = {}
    const attributes = transformSubjectAttributes(defaultAttributes, {
      ...subjectAttributes,
      shouldLog: false,
    })

    const overrideKeys = Object.keys(overrides)

    allFlags.forEach(flagKey => {
      if (overrideKeys.includes(flagKey)) {
        experiments[flagKey] = overrides[flagKey]
        return
      }

      const flagConfiguration = flagConfigurations[flagKey]
      if (!flagConfiguration) return

      const subjectKey = tryResolveEppoSubjectKey(
        flagConfiguration,
        sessionId,
        customerId,
      )

      if (!subjectKey) return

      const { variationType } = flagConfiguration
      switch (variationType) {
        case VariationType.STRING:
          experiments[flagKey] = client.getStringAssignment(
            flagKey,
            subjectKey,
            attributes,
            'control',
          )
          break
        case VariationType.BOOLEAN:
          experiments[flagKey] = client
            .getBooleanAssignment(flagKey, subjectKey, attributes, false)
            .toString()
          break
        case VariationType.INTEGER:
          experiments[flagKey] = client
            .getIntegerAssignment(flagKey, subjectKey, attributes, 0)
            .toString()
          break
      }
    })

    // Set the assignment cache back to the default
    // https://github.com/Eppo-exp/node-server-sdk/blob/0992f27465a0962efdfcfabe05138b91d5d03695/src/index.ts#L109
    client?.useLRUInMemoryAssignmentCache(50_000)

    return experiments
  }

  return getAllExperimentVariants
}
